<script setup>
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import ComboboxField from "@js/components/fields/ComboboxField.vue";
import TextareaField from "@js/components/fields/TextareaField.vue";
import { useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { array, boolean, object, string } from "yup";
import SwitchField from "@js/components/fields/SwitchField.vue";

const {
  tenant,
  targetedLanguages,
  websiteVariants,
  targetableGoogleWebsites,
  callback,
  keywords = "",
  disabledKeywords = false,
} = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
  targetedLanguages: {
    type: Array,
    required: true,
  },
  websiteVariants: {
    type: Array,
    required: true,
  },
  targetableGoogleWebsites: {
    type: Array,
    required: true,
  },
  callback: {
    type: Function,
    required: false,
  },
  keywords: {
    type: String,
    required: false,
  },
  disabledKeywords: {
    type: Boolean,
    required: false,
  },
});

const { t } = useI18n();

const isOpen = ref(false);

const isSubmitting = ref(false);

const isLoading = ref(false);

const { route } = useRoute();

const form = reactive({
  keywords,
  importantToFollow: false,
  language: null,
  targets: websiteVariants.reduce((acc, variant) => {
    acc[variant.id] = [];
    return acc;
  }, {}),
});

const validationSchema = object({
  keywords: string().required(t("error.required")),
  importantToFollow: boolean(),
  language: object().required(t("error.required")),
  targets: array().of(
    array().test(
      "at-least-one-target",
      t("error.atLeastOneTargetRequired"),
      () => {
        return Object.values(form.targets).some(
          (targetArray) => targetArray.length > 0,
        );
      },
    ),
  ),
});

function setIsOpen(value) {
  isOpen.value = value;
}

function importAction() {
  isSubmitting.value = true;

  fetch(
    route("front.projectManagement.seo.postImportKeywords", {
      tenant: tenant.id,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        keywords: form.keywords
          .split("\n")
          .filter((keyword) => keyword.trim() !== ""),
        importantToFollow: form.importantToFollow,
        languageId: form.language.id,
        targets: Object.fromEntries(
          Object.entries(form.targets).map(([key, value]) => [
            key,
            value.map((item) => item.id),
          ]),
        ),
      }),
    },
  )
    .then((response) => response.json())
    .then(() => {
      setIsOpen(false);

      if (callback) {
        callback();
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      isSubmitting.value = false;
    });
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen">
    <template #title>
      <div class="flex items-center">
        {{ t("title.importKeywords") }}
      </div>
    </template>

    <template #full-content>
      <Form
        :validation-schema="validationSchema"
        @submit="importAction"
        v-slot="{ errors }"
        class="flex h-full flex-col"
      >
        <div class="h-full max-h-[calc(100vh-8rem)] overflow-y-auto px-6 py-4">
          <TextareaField
            v-model="form.keywords"
            :label="t('label.keywords')"
            :placeholder="t('label.sourceUrls')"
            field-name="keywords"
            field-id="keywords"
            :legend="t('text.oneKeywordPerLine')"
            required
            :disabled="disabledKeywords"
          />

          <SwitchField
            v-model="form.importantToFollow"
            :label="t('label.importantToFollow')"
            field-name="importantToFollow"
            field-id="importantToFollow"
          />

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <ComboboxField
            v-model="form.language"
            :options="targetedLanguages"
            field-name="language"
            field-id="language"
            :label="t('label.language')"
            filtered-option-key="name"
            has-empty-option
            required
          >
            <template #optionContent="{ option }">
              {{ option.name }}
            </template>
          </ComboboxField>

          <ComboboxField
            v-for="websiteVariant in websiteVariants"
            :key="websiteVariant.id"
            v-model="form.targets[websiteVariant.id]"
            filtered-option-key="name"
            has-empty-option
            :options="targetableGoogleWebsites"
            :multiple="true"
            :field-name="'targets[' + websiteVariant.id + ']'"
            :field-id="'targets[' + websiteVariant.id + ']'"
            :label="t('label.targets') + ' (' + websiteVariant.name + ')'"
            by="id"
          >
            <template #optionContent="{ option }">
              {{ option.name }}
            </template>
          </ComboboxField>
        </div>

        <div
          class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 p-6"
        >
          <ActionButton
            submit
            :disabled="
              Object.keys(errors).length > 0 || isSubmitting || isLoading
            "
            :loading="isSubmitting"
          >
            <template v-if="!isSubmitting">
              {{ t("button.import") }}
            </template>

            <template v-else>
              {{ t("button.importInProgress") }}
            </template>
          </ActionButton>
        </div>
      </Form>
    </template>
  </Drawer>
</template>
